import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandData } from '@/mocks/aboutData';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function About() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于NOPAY钱包',
        description: 'NOPAY钱包品牌介绍 - 2026国际足联世界杯官方合作伙伴，全球领先的数字化娱乐平台',
        url: 'http://dajiang03.cn/about',
        isPartOf: {
          '@type': 'WebSite',
          name: 'NOPAY钱包',
          url: 'http://dajiang03.cn/',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((item, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: item.label,
          ...(item.path ? { item: `http://dajiang03.cn/${item.path}` } : {}),
        })),
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: brandData.faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setContactForm({ name: '', email: '', phone: '', message: '' });
      setSubmitting(false);
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && String(value).trim() !== '') {
          params.append(key, String(value));
        }
      });

      const response = await fetch('http://dajiang03.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后再试');
    } finally {
      setSubmitting(false);
      if (formStatus !== 'error') {
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们"
        description="了解NOPAY钱包品牌故事、核心价值观与全球布局。2026国际足联世界杯官方合作伙伴，全球领先的数字化娱乐平台。"
        keywords="NOPAY钱包,关于NOPAY,品牌介绍,2026世界杯,NOPAY,数字化娱乐"
        canonicalUrl="http://dajiang03.cn/about"
        schemaJson={aboutSchema}
      />

      <Navbar />

      <main>
        <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-4xl text-center">
            <p className="text-accent-600 text-sm font-semibold tracking-wide mb-2">{brandData.subtitle}</p>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6">
              {brandData.title}
            </h1>
            <div className="mb-10">
              <img
                src={brandData.introImage}
                alt="NOPAY钱包品牌"
                className="w-full max-w-2xl mx-auto rounded-lg object-cover"
              />
            </div>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              {brandData.introText}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {brandData.sections.map((section, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${index === 0 ? 'ri-book-open-line' : index === 1 ? 'ri-heart-line' : 'ri-global-line'} text-accent-600 text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                    {section.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              平台<span className="text-accent-500">数据</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { label: '注册用户', value: '500万+', icon: 'ri-user-3-line' },
                { label: '覆盖国家/地区', value: '30+', icon: 'ri-earth-line' },
                { label: '安全运营天数', value: '2800+', icon: 'ri-shield-check-line' },
                { label: '合作伙伴', value: '100+', icon: 'ri-service-line' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              发展<span className="text-accent-500">历程</span>
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {brandData.milestones.map((item, index) => (
                  <div key={index} className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      {index % 2 === 0 ? (
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      ) : null}
                    </div>
                    <div className="relative z-10 w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-accent-500 text-white text-xs font-bold md:mx-0">
                      <div className="md:hidden absolute left-10 top-1/2 -translate-y-1/2 whitespace-nowrap">
                        <span className="font-heading text-sm font-bold text-foreground-900 mr-2">{item.year}</span>
                        <span className="text-xs text-foreground-600">{item.event}</span>
                      </div>
                    </div>
                    <div className="flex-1 hidden md:block">
                      {index % 2 !== 0 ? (
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      ) : (
                        <p className="text-sm text-foreground-500 font-heading">{item.year}</p>
                      )}
                      {index % 2 === 0 && (
                        <p className="text-sm text-foreground-500 font-heading">{item.year}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              核心<span className="text-accent-500">团队</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {brandData.teamData.map((member, index) => (
                <div key={index} className="text-center">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-36 h-36 mx-auto rounded-full object-cover mb-4"
                  />
                  <h3 className="font-heading text-base font-bold text-foreground-900">{member.name}</h3>
                  <p className="text-sm text-accent-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed max-w-xs mx-auto">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              常见<span className="text-accent-500">问题</span>
            </h2>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />

            <div className="max-w-3xl mx-auto space-y-3">
              {brandData.faqData.map((item, index) => (
                <details key={index} className="bg-background-50 rounded-lg p-5 cursor-pointer group">
                  <summary className="flex items-center justify-between text-sm font-semibold text-foreground-900 list-none">
                    {item.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              商务<span className="text-accent-500">合作</span>
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              诚邀各类合作伙伴，共谋数字化娱乐新未来。我们期待与品牌方、渠道商、广告商建立深度合作。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-team-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">战略合作</h3>
                <p className="text-sm text-foreground-600">联合举办大型活动，品牌深度绑定，共同开拓市场</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-store-3-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">渠道合作</h3>
                <p className="text-sm text-foreground-600">代理商、分销商合作模式，共享收益分成</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-advertisement-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">广告合作</h3>
                <p className="text-sm text-foreground-600">平台流量曝光，精准用户触达，效果广告合作</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 bg-background-50 rounded-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-mail-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">商务邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">hello@dajiang03.cn</p>
                </div>
              </div>
              <div className="hidden sm:block w-px h-10 bg-background-200"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">商务热线</p>
                  <p className="text-sm font-medium text-foreground-900">400-888-6688 转分机 8</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-12 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-6xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              联系<span className="text-accent-500">我们</span>
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              如有任何问题，欢迎随时与我们取得联系
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">公司地址</p>
                    <p className="text-sm text-foreground-600">{brandData.contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">客服热线</p>
                    <p className="text-sm text-foreground-600">{brandData.contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{brandData.contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">工作时间</p>
                    <p className="text-sm text-foreground-600">{brandData.contactInfo.businessHours}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="bg-background-100 rounded-lg p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">电话</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                    required
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 bg-primary-500 text-white font-semibold rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">留言提交成功！我们会尽快与您联系。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后再试'}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}